import { Button, Icon } from '@wtfalch/design';

import type { EndedCall } from './CallSession.tsx';

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * What the room shows after the person leaves its call: that it is over, how
 * long and with how many, and the two things to do next. The follow-up card is
 * offered where the call had no meeting. Tasks and a recording are not drawn:
 * nothing in Calls produces either yet.
 */
export function CallEnded({
  ended,
  onBack,
  onAgain,
  onBook,
  className,
}: {
  ended: EndedCall;
  /** Back to the room. */
  onBack: () => void;
  /** Starts the same call again. */
  onAgain: () => void;
  onBook?: () => void;
  className?: string;
}) {
  const { target, minutes, people } = ended;
  const where = target.direct ? `with ${target.title}` : `in #${target.title}`;
  return (
    <section
      aria-label="The call is over"
      className={['flex flex-col items-center gap-5 p-8 text-center', className]
        .filter(Boolean)
        .join(' ')}
    >
      <h1 className="h1 m-0">That call is over.</h1>
      <p className="m-0 text-muted">
        {plural(minutes, 'minute', 'minutes')}, {plural(people, 'person', 'people')}, {where}.
      </p>
      {onBook && (
        <section className="flex flex-col items-center gap-2 p-4 rounded-md border border-border max-w-96">
          <h2 className="h3 m-0">A follow-up nobody booked</h2>
          <p className="m-0 text-sm text-muted">
            Agreed to meet again? Put it in the calendar now.
          </p>
          <Button kind="primary" size="sm" onPress={onBook}>
            <Icon name="calendar" size={16} />
            <span>Put it in</span>
          </Button>
        </section>
      )}
      <span className="flex flex-wrap justify-center gap-3">
        <Button kind="primary" onPress={onBack}>
          <Icon name="back" size={16} />
          <span>Back to the room</span>
        </Button>
        <Button onPress={onAgain}>
          <Icon name="phone" size={16} />
          <span>Call them again</span>
        </Button>
      </span>
    </section>
  );
}
